/**
 * @module components/Calendar/CalendarPanel
 * @description Calendario global de eventos de todos los frameworks normativos.
 *
 * Agrega en una sola vista de calendario todos los eventos con fechas relevantes:
 * - ISO 27002: fechas límite de tareas de controles
 * - ISO 27001: fechas límite de tareas de requisitos SGSI
 * - LMCS: plazos de artículos y fechas límite de tareas
 * - ACS: sesiones agendadas (reuniones Jitsi)
 *
 * Los eventos se clasifican por norma, tipo y proximidad temporal.
 * Los eventos vencidos se destacan en rojo; los próximos 7 días en amarillo.
 *
 * @see ACSContext para las reuniones ACS
 * @see DataContext para las tareas de controles ISO 27002
 * @see LMCSContext para las tareas y plazos de artículos LMCS
 */
// ============================================================
// CalendarPanel — Panel global de eventos agendados
// Agrega eventos de todos los frameworks:
//   ISO 27002 tareas, ISO 27001 tareas, LMCS tareas + plazos,
//   ACS reuniones
// Clasificados por norma, tipo y proximidad temporal.
// ============================================================

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  X, ChevronLeft, ChevronRight, Calendar,
  Video, ClipboardList, AlertTriangle, Clock,
} from 'lucide-react';
import { useData }      from '../../contexts/DataContext';
import { useISO27001 }  from '../../contexts/ISO27001Context';
import { useLMCS }      from '../../contexts/LMCSContext';
import { useACS }       from '../../contexts/ACSContext';

// ──────────────────────────────────────────────────────────
// TIPOS
// ──────────────────────────────────────────────────────────

type CalendarFramework = 'ISO27002' | 'ISO27001' | 'LMCS' | 'ACS';
type CalendarEventType = 'task' | 'acs-meeting' | 'regulatory-deadline';

interface CalendarEvent {
  id:            string;
  title:         string;
  date:          string;              // ISO date string
  type:          CalendarEventType;
  framework:     CalendarFramework;
  status:        string;
  priority?:     'low' | 'medium' | 'high' | 'critical';
  contextLabel?: string;
}

// ──────────────────────────────────────────────────────────
// CONSTANTES DE ESTILO
// ──────────────────────────────────────────────────────────

const FRAMEWORK_DOT: Record<CalendarFramework, string> = {
  ISO27002: 'bg-blue-500',
  ISO27001: 'bg-indigo-500',
  LMCS:     'bg-red-500',
  ACS:      'bg-violet-500',
};

const FRAMEWORK_BADGE: Record<CalendarFramework, string> = {
  ISO27002: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  ISO27001: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  LMCS:     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  ACS:      'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
};

const TYPE_ICON: Record<CalendarEventType, React.ElementType> = {
  'task':                 ClipboardList,
  'acs-meeting':          Video,
  'regulatory-deadline':  AlertTriangle,
};

const TYPE_LABEL: Record<string, string> = {
  'task-ISO27002':          'Tarea ISO 27002',
  'task-ISO27001':          'Tarea ISO 27001',
  'task-LMCS':              'Tarea LMCS',
  'acs-meeting-ACS':        'Reunión ACS',
  'regulatory-deadline-LMCS': 'Plazo regulatorio',
};

const PRIORITY_DOT: Record<string, string> = {
  critical: 'bg-red-500',
  high:     'bg-orange-500',
  medium:   'bg-yellow-500',
  low:      'bg-green-500',
};

// ──────────────────────────────────────────────────────────
// HELPERS DE FECHA
// ──────────────────────────────────────────────────────────

function toDateStr(d: Date) {
  return d.toISOString().split('T')[0];
}

function isOverdue(event: CalendarEvent): boolean {
  const today = toDateStr(new Date());
  return event.date < today && event.status !== 'completed' && event.status !== 'cancelled';
}

function isSameDay(a: string, b: string) {
  return a.split('T')[0] === b.split('T')[0];
}

// ──────────────────────────────────────────────────────────
// MINI CALENDAR
// ──────────────────────────────────────────────────────────

const DAYS_SHORT = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const MONTHS_ES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

interface MiniCalendarProps {
  year:          number;
  month:         number;          // 0-indexed
  events:        CalendarEvent[];
  selectedDay:   string | null;   // 'YYYY-MM-DD'
  onSelectDay:   (day: string | null) => void;
  onPrevMonth:   () => void;
  onNextMonth:   () => void;
}

function MiniCalendar({ year, month, events, selectedDay, onSelectDay, onPrevMonth, onNextMonth }: MiniCalendarProps) {
  const today = toDateStr(new Date());

  // Días del mes
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);

  // Lunes=0, primer día de la semana
  const startOffset = (firstDay.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0
  const totalDays   = lastDay.getDate();

  // Mapa de eventos por día: 'YYYY-MM-DD' → Set<framework>
  const eventDayMap = useMemo(() => {
    const map = new Map<string, Set<CalendarFramework>>();
    for (const ev of events) {
      const day = ev.date.split('T')[0];
      if (!map.has(day)) map.set(day, new Set());
      map.get(day)!.add(ev.framework);
    }
    return map;
  }, [events]);

  const cells: (number | null)[] = [
    ...Array(startOffset).fill(null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];

  // Pad to full weeks
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4">
      {/* Navegación mes */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={onPrevMonth}
          className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
        </button>
        <span className="text-sm font-semibold text-gray-800 dark:text-white">
          {MONTHS_ES[month]} {year}
        </span>
        <button
          onClick={onNextMonth}
          className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
        </button>
      </div>

      {/* Cabecera días */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS_SHORT.map(d => (
          <div key={d} className="text-center text-[10px] font-medium text-gray-400 dark:text-gray-500 py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Grilla de días */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} />;

          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const frameworks = eventDayMap.get(dateStr);
          const isToday    = dateStr === today;
          const isSelected = selectedDay === dateStr;

          return (
            <button
              key={dateStr}
              onClick={() => onSelectDay(isSelected ? null : dateStr)}
              className={`relative flex flex-col items-center py-1 rounded-lg text-xs transition-colors ${
                isSelected
                  ? 'bg-violet-600 text-white'
                  : isToday
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-bold'
                  : 'hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
              }`}
            >
              <span className="text-[11px] leading-tight">{day}</span>
              {/* Dots de eventos (máx 3) */}
              {frameworks && frameworks.size > 0 && (
                <div className="flex gap-0.5 mt-0.5">
                  {Array.from(frameworks).slice(0, 3).map(fw => (
                    <span
                      key={fw}
                      className={`w-1 h-1 rounded-full ${
                        isSelected ? 'bg-white/70' : FRAMEWORK_DOT[fw]
                      }`}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <p className="text-[10px] text-center text-violet-600 dark:text-violet-400 mt-2 font-medium">
          Filtrando: {new Date(selectedDay + 'T12:00:00').toLocaleDateString('es-CL', { day: 'numeric', month: 'long' })}
          <button onClick={() => onSelectDay(null)} className="ml-1 underline">ver todo</button>
        </p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// TARJETA DE EVENTO
// ──────────────────────────────────────────────────────────

function EventCard({ event }: { event: CalendarEvent }) {
  const Icon    = TYPE_ICON[event.type];
  const typeKey = `${event.type}-${event.framework}`;
  const label   = TYPE_LABEL[typeKey] ?? event.type;
  const overdue = isOverdue(event);

  const statusBadge =
    event.status === 'completed' || event.status === 'compliant'
      ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
      : event.status === 'in_progress'
      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
      : event.status === 'cancelled'
      ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
      : overdue
      ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';

  const statusLabel =
    event.status === 'completed' || event.status === 'compliant' ? 'Completado'
    : event.status === 'in_progress' ? 'En curso'
    : event.status === 'scheduled'   ? 'Agendada'
    : event.status === 'cancelled'   ? 'Cancelado'
    : overdue ? 'Vencido'
    : 'Pendiente';

  const dateLabel = new Date(event.date + (event.date.includes('T') ? '' : 'T12:00:00')).toLocaleDateString('es-CL', {
    day: '2-digit', month: 'short', year: '2-digit',
  });

  return (
    <div className={`flex items-start gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
      overdue ? 'border-l-2 border-l-red-400' : ''
    }`}>
      {/* Icono */}
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${FRAMEWORK_BADGE[event.framework]}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>

      {/* Contenido */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${FRAMEWORK_BADGE[event.framework]}`}>
            {event.framework === 'ACS' ? 'ACS' : event.framework}
          </span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500">{label}</span>
        </div>
        <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{event.title}</p>
        {event.contextLabel && (
          <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5">{event.contextLabel}</p>
        )}
        {/* Prioridad + fecha */}
        <div className="flex items-center gap-2 mt-1">
          {event.priority && (
            <span className={`inline-flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400`}>
              <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[event.priority]}`} />
              {event.priority === 'critical' ? 'Crítica' : event.priority === 'high' ? 'Alta' : event.priority === 'medium' ? 'Media' : 'Baja'}
            </span>
          )}
          <span className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500">
            <Clock className="w-2.5 h-2.5" />
            {dateLabel}
          </span>
        </div>
      </div>

      {/* Status badge */}
      <span className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium ${statusBadge}`}>
        {statusLabel}
      </span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ──────────────────────────────────────────────────────────

export interface CalendarPanelProps {
  onClose: () => void;
}

const FILTER_FRAMEWORKS: { value: CalendarFramework | 'ALL'; label: string }[] = [
  { value: 'ALL',     label: 'Todos'    },
  { value: 'ISO27002', label: 'ISO 27002' },
  { value: 'ISO27001', label: 'ISO 27001' },
  { value: 'LMCS',    label: 'LMCS'    },
  { value: 'ACS',     label: 'ACS'     },
];

export function CalendarPanel({ onClose }: CalendarPanelProps) {
  const today = new Date();

  const [displayYear,  setDisplayYear]  = useState(today.getFullYear());
  const [displayMonth, setDisplayMonth] = useState(today.getMonth());
  const [selectedDay,  setSelectedDay]  = useState<string | null>(null);
  const [fwFilter,     setFwFilter]     = useState<CalendarFramework | 'ALL'>('ALL');
  const [showOverdue,  setShowOverdue]  = useState(true);
  const [showTasks,    setShowTasks]    = useState(true);
  const [showMeetings, setShowMeetings] = useState(true);
  const [showDeadlines,setShowDeadlines]= useState(true);

  const panelRef = useRef<HTMLDivElement>(null);

  // Cerrar al clic fuera
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Cerrar con Escape
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Cargar datos de los contextos ──
  const { controls }     = useData();
  const iso27001         = useISO27001();
  const lmcs             = useLMCS();
  const acs              = useACS();

  // ── Agregar todos los eventos ──
  const allEvents = useMemo<CalendarEvent[]>(() => {
    const events: CalendarEvent[] = [];

    // ISO 27002 tareas
    for (const ctrl of controls) {
      for (const task of (ctrl.tasks ?? [])) {
        if (!task.dueDate) continue;
        events.push({
          id:       `iso27002-task-${task.id}`,
          title:    task.title,
          date:     task.dueDate,
          type:     'task',
          framework: 'ISO27002',
          status:   task.status,
          priority: task.priority,
          contextLabel: `${ctrl.code} — ${ctrl.title}`,
        });
      }
    }

    // ISO 27001 tareas
    for (const req of (iso27001.requirements ?? [])) {
      for (const task of (req.tasks ?? [])) {
        if (!task.dueDate) continue;
        events.push({
          id:       `iso27001-task-${task.id}`,
          title:    task.title,
          date:     task.dueDate,
          type:     'task',
          framework: 'ISO27001',
          status:   task.status,
          priority: task.priority,
          contextLabel: `${req.code} — ${req.title}`,
        });
      }
    }

    // LMCS tareas
    for (const art of (lmcs.articles ?? [])) {
      for (const task of (art.tasks ?? [])) {
        if (!task.dueDate) continue;
        events.push({
          id:       `lmcs-task-${task.id}`,
          title:    task.title,
          date:     task.dueDate,
          type:     'task',
          framework: 'LMCS',
          status:   task.status,
          priority: task.priority,
          contextLabel: `Artículo ${art.articleNumber} — ${art.name}`,
        });
      }

      // Plazos regulatorios
      if (art.regulatoryDeadline) {
        events.push({
          id:       `lmcs-deadline-${art.id}`,
          title:    `Plazo: ${art.name}`,
          date:     art.regulatoryDeadline,
          type:     'regulatory-deadline',
          framework: 'LMCS',
          status:   art.status,
          contextLabel: `Artículo ${art.articleNumber}`,
        });
      }
    }

    // ACS reuniones
    for (const meeting of (acs.meetings ?? [])) {
      events.push({
        id:       `acs-meeting-${meeting.id}`,
        title:    meeting.title,
        date:     meeting.scheduledAt,
        type:     'acs-meeting',
        framework: 'ACS',
        status:   meeting.status,
        contextLabel: `${meeting.frameworkCode} · ${meeting.contextLabel}`,
      });
    }

    return events;
  }, [controls, iso27001.requirements, lmcs.articles, acs.meetings]);

  // ── Filtrar eventos ──
  const filteredEvents = useMemo(() => {
    return allEvents.filter(ev => {
      if (fwFilter !== 'ALL' && ev.framework !== fwFilter) return false;
      if (!showTasks     && ev.type === 'task')                return false;
      if (!showMeetings  && ev.type === 'acs-meeting')         return false;
      if (!showDeadlines && ev.type === 'regulatory-deadline') return false;
      if (!showOverdue   && isOverdue(ev))                     return false;
      if (selectedDay    && !isSameDay(ev.date, selectedDay))  return false;
      return true;
    });
  }, [allEvents, fwFilter, showTasks, showMeetings, showDeadlines, showOverdue, selectedDay]);

  // ── Agrupar eventos por proximidad ──
  const groupedEvents = useMemo(() => {
    const todayStr     = toDateStr(today);
    const weekEnd      = toDateStr(new Date(today.getTime() + 7 * 86400000));
    const monthEnd     = toDateStr(new Date(today.getFullYear(), today.getMonth() + 1, 0));

    const groups: { label: string; color: string; events: CalendarEvent[] }[] = [
      { label: 'Vencidos',    color: 'text-red-600 dark:text-red-400',     events: [] },
      { label: 'Hoy',         color: 'text-green-700 dark:text-green-400', events: [] },
      { label: 'Esta semana', color: 'text-blue-700 dark:text-blue-400',   events: [] },
      { label: 'Este mes',    color: 'text-indigo-700 dark:text-indigo-400',events: [] },
      { label: 'Futuros',     color: 'text-gray-600 dark:text-gray-400',   events: [] },
    ];

    for (const ev of filteredEvents) {
      const d = ev.date.split('T')[0];
      if (isOverdue(ev))        groups[0].events.push(ev);
      else if (d === todayStr)  groups[1].events.push(ev);
      else if (d <= weekEnd)    groups[2].events.push(ev);
      else if (d <= monthEnd)   groups[3].events.push(ev);
      else                      groups[4].events.push(ev);
    }

    // Ordenar cada grupo por fecha
    for (const g of groups) {
      g.events.sort((a, b) => a.date.localeCompare(b.date));
    }

    return groups.filter(g => g.events.length > 0);
  }, [filteredEvents, today]);

  function prevMonth() {
    if (displayMonth === 0) { setDisplayYear(y => y - 1); setDisplayMonth(11); }
    else setDisplayMonth(m => m - 1);
  }

  function nextMonth() {
    if (displayMonth === 11) { setDisplayYear(y => y + 1); setDisplayMonth(0); }
    else setDisplayMonth(m => m + 1);
  }

  const totalVisible = filteredEvents.length;
  const totalAll     = allEvents.length;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]" />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed top-0 right-0 z-50 h-full w-[460px] max-w-full bg-white dark:bg-gray-800 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700 overflow-hidden"
      >
        {/* Header del panel */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Calendario de Eventos</h2>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {totalVisible} de {totalAll} eventos
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">

          {/* Mini calendario */}
          <div className="px-4 pt-4 pb-2">
            <MiniCalendar
              year={displayYear}
              month={displayMonth}
              events={allEvents}
              selectedDay={selectedDay}
              onSelectDay={setSelectedDay}
              onPrevMonth={prevMonth}
              onNextMonth={nextMonth}
            />
          </div>

          {/* Filtros por framework */}
          <div className="px-4 pb-2">
            <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
              Norma
            </p>
            <div className="flex flex-wrap gap-1">
              {FILTER_FRAMEWORKS.map(f => (
                <button
                  key={f.value}
                  onClick={() => setFwFilter(f.value)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    fwFilter === f.value
                      ? f.value === 'ALL'      ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900'
                        : f.value === 'ISO27002' ? 'bg-blue-600 text-white'
                        : f.value === 'ISO27001' ? 'bg-indigo-600 text-white'
                        : f.value === 'LMCS'    ? 'bg-red-600 text-white'
                        :                         'bg-violet-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Filtros por tipo */}
          <div className="px-4 pb-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
              Tipo de evento
            </p>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'showTasks',     setter: setShowTasks,     value: showTasks,     label: 'Tareas',             icon: ClipboardList },
                { key: 'showMeetings',  setter: setShowMeetings,  value: showMeetings,  label: 'Reuniones ACS',      icon: Video },
                { key: 'showDeadlines', setter: setShowDeadlines, value: showDeadlines, label: 'Plazos regulatorios', icon: AlertTriangle },
                { key: 'showOverdue',   setter: setShowOverdue,   value: showOverdue,   label: 'Vencidos',           icon: Clock },
              ] as const).map(({ key, setter, value, label, icon: Ic }) => (
                <label key={key} className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-600 dark:text-gray-400 select-none">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={e => setter(e.target.checked)}
                    className="accent-violet-600 w-3 h-3"
                  />
                  <Ic className="w-3 h-3" />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Lista de eventos */}
          <div>
            {groupedEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-600">
                <Calendar className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm">No hay eventos para mostrar</p>
                <p className="text-xs mt-1">
                  {selectedDay ? 'No hay eventos en este día.' : 'Ajusta los filtros para ver más.'}
                </p>
              </div>
            ) : (
              groupedEvents.map(group => (
                <div key={group.label}>
                  {/* Cabecera de grupo */}
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/40 sticky top-0 z-10 border-b border-gray-100 dark:border-gray-700">
                    <span className={`text-[11px] font-semibold uppercase tracking-wide ${group.color}`}>
                      {group.label}
                    </span>
                    <span className="ml-1.5 text-[10px] text-gray-400">({group.events.length})</span>
                  </div>

                  {/* Eventos del grupo */}
                  {group.events.map(ev => (
                    <EventCard key={ev.id} event={ev} />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
